import type { CurrencyEnum } from "@litres-libs/types";

/**
 * @description Метод возвращает локализованное число с символов валюты(опционально) до 2 знаков после запятой, если есть остаток после запятой.
 * Примечание: В React рекомендуется использовать с suppressHydrationWarning, так как поведение в Node.JS может отличаться от браузеров.
 *
 * @param {number | undefined} value - Входное число
 * @param {CurrencyEnum | undefined} currency - Валюта (RUB, EUR, USD etc.), либо просто число.
 * @param {string | undefined} locale - Локаль (по дефолту ru-RU)
 * @return {string | undefined} - Строка с числом в выбранном формате
 *
 * @example
 *
 *     getPrice(560.1423, CurrencyEnum.USD, 'en-US')
 */
export const getPrice = (
  value: number | null | undefined,
  currency: CurrencyEnum | null | undefined,
  locale: string | undefined = undefined
): string | undefined => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    let fractionDigits = 0;

    const isIntegerValue = value % 1 === 0;

    if (!isIntegerValue) {
      fractionDigits = 2;
    }

    const intlParams = {
      style: currency ? "currency" : "decimal",
      currency: currency?.toUpperCase(),
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    };

    try {
      return new Intl.NumberFormat(locale ?? "ru-RU", intlParams).format(value);
    } catch (error) {
      // Отлавливаем ошибку currencyDisplay must be either "code", "symbol", or "name"
      // для старых браузеров, где нет поддержки narrowSymbol
      return new Intl.NumberFormat(locale ?? "ru-RU", {
        ...intlParams,
        currencyDisplay: "symbol",
      }).format(value);
    }
  }
  return undefined;
};
